/**
 * Record Mutation Hooks
 *
 * Provides mutations for Record operations with optimistic updates:
 * - createMutation: Create new Record from published RecordType
 * - updateMutation: Update Record data
 * - archiveMutation: Archive a Record (soft-delete)
 * - restoreMutation: Restore an archived Record (admin/quality)
 * - deleteMutation: Permanently delete a Record (admin/quality)
 * - completeStepMutation: Complete a workflow step
 * - signFieldMutation: Sign an e-signature field
 *
 * Uses createEntityCache for clean cache coordination following MATIC patterns.
 */

import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {useToast} from '@/matic/components/ui/toast';
import {createEntityCache} from '@/matic/lib/utils/cache-coordination';
import {getRecordsApi} from '../api/records-api';
import {
  RECORD_ENTITY_KEY,
  RECORD_LIST_KEY,
  recordKeys,
  recordTypeKeys,
} from '../query-keys';
import {scheduleOccurrenceKeys} from '@/matic/features/scheduling/query-keys';
import {relayKeys} from '@/matic/features/relay/query-keys';
import type {
  QmsRecord,
  CreateRecordInput,
  UpdateRecordInput,
  ArchiveRecordInput,
  CompleteStepInput,
  StepAssigneeInput,
} from '../types';

/**
 * Input for update mutation (includes recordId)
 */
export type UpdateRecordMutationInput = UpdateRecordInput & {
  recordId: string;
};

/**
 * Input for archive mutation
 */
export type ArchiveRecordMutationInput = ArchiveRecordInput & {
  recordId: string;
};

/**
 * Input for delete mutation
 */
export type DeleteRecordMutationInput = {
  recordId: string;
};

/**
 * Input for complete step mutation
 */
export type CompleteStepMutationInput = CompleteStepInput & {
  recordId: string;
  stepOrder: number;
};

/**
 * Input for sign field mutation
 */
export type SignFieldMutationInput = {
  recordId: string;
  fieldKey: string;
};

/**
 * Input for update metadata mutation (name/displayId)
 */
export type UpdateMetadataMutationInput = {
  recordId: string;
  name?: string;
  displayId?: string;
};

/**
 * Input for upload file mutation
 */
export type UploadFileMutationInput = {
  recordId: string;
  fieldKey: string;
  file: File;
};

/**
 * Input for delete file mutation
 */
export type DeleteFileMutationInput = {
  recordId: string;
  fileId: string;
};

/**
 * Input for update step assignees mutation
 */
export type UpdateStepAssigneesMutationInput = {
  recordId: string;
  stepAssignees: StepAssigneeInput[];
};

/**
 * Hook for Record mutations
 */
export function useRecordMutations() {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const {toast} = useToast();

  const recordCache = createEntityCache<QmsRecord>(
    queryClient,
    RECORD_ENTITY_KEY,
    RECORD_LIST_KEY
  );

  // The Library activity feed lists QMS records via the relay trace query
  // (`relatedRecords`), which excludes soft-deleted records. Lifecycle changes
  // that affect a record's feed presence or status badge (delete, archive,
  // restore, close, final workflow step completion) must bust the trace cache
  // or the feed shows stale rows.
  const invalidateLibraryFeed = () => {
    void queryClient.invalidateQueries({queryKey: relayKeys.trace.all()});
  };

  /**
   * Create new Record
   */
  const createMutation = useMutation({
    async mutationFn(input: CreateRecordInput) {
      const api = getRecordsApi();
      return api.createRecord(input);
    },

    async onMutate() {
      // Cancel active record list queries to prevent race conditions.
      await queryClient.cancelQueries({
        queryKey: recordKeys.lists(tenantId),
      });
    },

    onSuccess(newRecord) {
      toast({
        title: 'Record created',
        description: `${newRecord.displayId} has been created`,
        variant: 'success',
      });
    },

    onError(error: Error) {
      toast({
        title: 'Failed to create record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled() {
      // Use refetchType 'all' so inactive list queries (user navigates away
      // after create) get refetched in the background — not just marked stale.
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.myTasks(tenantId),
        refetchType: 'all',
      });
      // Invalidate next-suffix so the preview is fresh if the user creates another record
      void queryClient.invalidateQueries({
        queryKey: recordTypeKeys.all,
        predicate: (query) => query.queryKey.includes('next-suffix'),
      });
    },
  });

  /**
   * Update Record data
   */
  const updateMutation = useMutation({
    async mutationFn(input: UpdateRecordMutationInput) {
      const api = getRecordsApi();
      const {recordId, ...updateData} = input;
      return api.updateRecord(recordId, updateData);
    },

    async onMutate(input: UpdateRecordMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);

      recordCache.updateSingle(input.recordId, tenantId, (old) => ({
        ...old,
        data: {...old.data, ...input.data},
        updatedAt: new Date().toISOString(),
      }));

      return snapshots;
    },

    // No success toast for updates - they happen frequently and would be disruptive
    // The SaveStatusIndicator provides subtle feedback instead

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to update record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
    },
  });

  /**
   * Archive Record (soft-delete)
   */
  /**
   * Close an open Record (manual close for attachment-only records)
   */
  const closeMutation = useMutation({
    async mutationFn(recordId: string) {
      const api = getRecordsApi();
      return api.closeRecord(recordId);
    },

    async onMutate(recordId: string) {
      await recordCache.cancelQueries(recordId, tenantId);
      return recordCache.getSnapshots(recordId, tenantId);
    },

    onSuccess() {
      toast({
        title: 'Record closed',
        variant: 'success',
      });
    },

    onError(error: Error, recordId, context) {
      if (context) {
        recordCache.restoreSnapshots(recordId, tenantId, context);
      }

      toast({
        title: 'Failed to close record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, recordId) {
      recordCache.invalidate(recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      // Scheduling occurrences lazily detect record closure
      void queryClient.invalidateQueries({
        queryKey: scheduleOccurrenceKeys.all,
      });
      invalidateLibraryFeed();
    },
  });

  const archiveMutation = useMutation({
    async mutationFn(input: ArchiveRecordMutationInput) {
      const api = getRecordsApi();
      const {recordId, ...archiveData} = input;
      return api.archiveRecord(recordId, archiveData);
    },

    async onMutate(input: ArchiveRecordMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);
      return snapshots;
    },

    onSuccess() {
      toast({
        title: 'Record archived',
        variant: 'success',
      });
    },

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to archive record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.myTasks(tenantId),
      });
      invalidateLibraryFeed();
    },
  });

  /**
   * Restore archived Record (admin/quality only)
   */
  const restoreMutation = useMutation({
    async mutationFn(recordId: string) {
      const api = getRecordsApi();
      return api.restoreRecord(recordId);
    },

    async onMutate(recordId: string) {
      await recordCache.cancelQueries(recordId, tenantId);
      const snapshots = recordCache.getSnapshots(recordId, tenantId);
      return snapshots;
    },

    onSuccess() {
      toast({
        title: 'Record restored',
        variant: 'success',
      });
    },

    onError(error: Error, recordId, context) {
      if (context) {
        recordCache.restoreSnapshots(recordId, tenantId, context);
      }

      toast({
        title: 'Failed to restore record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, recordId) {
      recordCache.invalidate(recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      invalidateLibraryFeed();
    },
  });

  /**
   * Permanently delete Record (admin/quality only, irreversible)
   */
  const deleteMutation = useMutation({
    async mutationFn(input: DeleteRecordMutationInput) {
      const api = getRecordsApi();
      return api.deleteRecord(input.recordId);
    },

    onSuccess() {
      toast({
        title: 'Record permanently deleted',
        variant: 'success',
      });
    },

    onError(error: Error) {
      toast({
        title: 'Failed to delete record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      invalidateLibraryFeed();
    },
  });

  /**
   * Complete workflow step
   */
  const completeStepMutation = useMutation({
    async mutationFn(input: CompleteStepMutationInput) {
      const api = getRecordsApi();
      const {recordId, stepOrder, ...stepData} = input;
      return api.completeStep(recordId, stepOrder, stepData);
    },

    async onMutate(input: CompleteStepMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);

      // Optimistically update step instance statuses for visual stepper feedback.
      // currentStepOrder is deliberately NOT updated here — let the backend
      // response drive it. This prevents race conditions where consumers
      // (e.g., Scribe) react to the new currentStepOrder before the backend
      // has committed the step completion.
      recordCache.updateSingle(input.recordId, tenantId, (old) => ({
        ...old,
        stepInstances: old.stepInstances.map((si) =>
          si.stepOrder === input.stepOrder
            ? {
                ...si,
                status: 'completed',
                completedAt: new Date().toISOString(),
              }
            : si.stepOrder === input.stepOrder + 1
              ? {...si, status: 'in_progress'}
              : si
        ),
        updatedAt: new Date().toISOString(),
      }));

      return snapshots;
    },

    onSuccess(updatedRecord) {
      // Land confirmed backend data in the cache immediately so downstream
      // effects see the actual new currentStepOrder without waiting for the
      // onSettled invalidation refetch.
      recordCache.updateSingle(updatedRecord.id, tenantId, () => updatedRecord);

      const stepCompleted = updatedRecord.stepInstances.find(
        (si) => si.status === 'completed'
      );

      if (updatedRecord.status === 'closed') {
        toast({
          title: 'Record completed',
          description: `${updatedRecord.displayId} has been closed`,
          variant: 'success',
        });
      } else {
        toast({
          title: 'Step completed',
          description: stepCompleted
            ? `${stepCompleted.title} has been completed`
            : undefined,
          variant: 'success',
        });
      }
    },

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to complete step',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.myTasks(tenantId),
      });
      // Scheduling occurrences lazily detect record closure
      void queryClient.invalidateQueries({
        queryKey: scheduleOccurrenceKeys.all,
      });
      invalidateLibraryFeed();
    },
  });

  /**
   * Reopen the most recently completed workflow step.
   */
  const reopenLastStepMutation = useMutation({
    async mutationFn(recordId: string) {
      const api = getRecordsApi();
      return api.reopenLastStep(recordId);
    },

    onSuccess(updatedRecord) {
      recordCache.updateSingle(updatedRecord.id, tenantId, () => updatedRecord);
      toast({
        title: 'Step reopened',
        description: `${updatedRecord.displayId} is back in progress`,
        variant: 'success',
      });
    },

    onError(error: Error) {
      toast({
        title: 'Failed to reopen step',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, recordId) {
      recordCache.invalidate(recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.myTasks(tenantId),
      });
    },
  });

  /**
   * Sign e-signature field
   */
  const signFieldMutation = useMutation({
    async mutationFn(input: SignFieldMutationInput) {
      const api = getRecordsApi();
      return api.signField(input.recordId, input.fieldKey);
    },

    async onMutate(input: SignFieldMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);
      return snapshots;
    },

    onSuccess() {
      toast({
        title: 'Signed successfully',
        variant: 'success',
      });
    },

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to sign',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
    },
  });

  /**
   * Update record metadata (name/displayId)
   * Separate from updateMutation which is for frequent field auto-saves (no toast).
   */
  const updateMetadataMutation = useMutation({
    async mutationFn(input: UpdateMetadataMutationInput) {
      const api = getRecordsApi();
      const {recordId, ...updates} = input;
      return api.updateRecord(recordId, updates);
    },

    async onMutate(input: UpdateMetadataMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);

      recordCache.updateSingle(input.recordId, tenantId, (old) => ({
        ...old,
        ...(input.name !== undefined && {name: input.name}),
        ...(input.displayId !== undefined && {displayId: input.displayId}),
        updatedAt: new Date().toISOString(),
      }));

      return snapshots;
    },

    onSuccess() {
      toast({
        title: 'Record updated',
        variant: 'success',
      });
    },

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to update record',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.lists(tenantId),
      });
    },
  });

  /**
   * Update step assignees
   */
  const updateStepAssigneesMutation = useMutation({
    async mutationFn(input: UpdateStepAssigneesMutationInput) {
      const api = getRecordsApi();
      return api.updateRecord(input.recordId, {
        stepAssignees: input.stepAssignees,
      });
    },

    async onMutate(input: UpdateStepAssigneesMutationInput) {
      await recordCache.cancelQueries(input.recordId, tenantId);
      const snapshots = recordCache.getSnapshots(input.recordId, tenantId);

      // Optimistic update for step assignees (users + groups)
      recordCache.updateSingle(input.recordId, tenantId, (old) => ({
        ...old,
        stepInstances: old.stepInstances.map((si) => {
          const newAssignees = input.stepAssignees.find(
            (sa) => sa.stepOrder === si.stepOrder
          );
          if (newAssignees) {
            return {
              ...si,
              assignedUserIds: newAssignees.assigneeIds,
              assignedGroupIds: newAssignees.groupIds,
            };
          }

          return si;
        }),
        updatedAt: new Date().toISOString(),
      }));

      return snapshots;
    },

    onSuccess() {
      toast({
        title: 'Assignees updated',
        variant: 'success',
      });
    },

    onError(error: Error, variables, context) {
      if (context) {
        recordCache.restoreSnapshots(variables.recordId, tenantId, context);
      }

      toast({
        title: 'Failed to update assignees',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
      void queryClient.invalidateQueries({
        queryKey: recordKeys.myTasks(tenantId),
      });
    },
  });

  /**
   * Upload file to a file_attachment field
   */
  const uploadFileMutation = useMutation({
    async mutationFn(input: UploadFileMutationInput) {
      const api = getRecordsApi();
      return api.uploadFile(input.recordId, input.fieldKey, input.file);
    },

    onError(error: Error) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
    },
  });

  /**
   * Delete file from a file_attachment field
   */
  const deleteFileMutation = useMutation({
    async mutationFn(input: DeleteFileMutationInput) {
      const api = getRecordsApi();
      return api.deleteFile(input.recordId, input.fileId);
    },

    onError(error: Error) {
      toast({
        title: 'Delete failed',
        description: error.message,
        variant: 'error',
      });
    },

    onSettled(_data, _error, variables) {
      recordCache.invalidate(variables.recordId, tenantId);
    },
  });

  return {
    createMutation,
    updateMutation,
    updateMetadataMutation,
    closeMutation,
    archiveMutation,
    restoreMutation,
    deleteMutation,
    completeStepMutation,
    reopenLastStepMutation,
    signFieldMutation,
    updateStepAssigneesMutation,
    uploadFileMutation,
    deleteFileMutation,
  };
}
